import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PerspectiveGrid from "./PerspectiveGrid";

// ─────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────
const TRUST_ITEMS = [
  "Annex XIII data model",
  "QSeal & EU registry handled",
  "Free readiness check",
];

// ─────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────
function Hero() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const anim = (delay = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(28px)",
    transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
  });

  // → Battery passport page
  const goPassport = () => {
    navigate("/battery-passport");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // → Battery passport page, scrolled to the handbook section
  const goHandbook = () => {
    navigate("/battery-passport");
    setTimeout(() => {
      const el = document.getElementById("handbook");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 500);
  };

  return (
    // Content sits at the top; the grid fills the space right below it (down to the fold).
    // Change 77px if your navbar height differs.
    <section
      id="home"
      className="relative flex flex-col bg-slate-50 overflow-hidden md:min-h-[calc(100svh-77px)]"
    >
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 md:pt-12 md:pb-4 text-center">
        <div
          style={anim(0)}
          className="font-mono text-xs sm:text-sm uppercase tracking-[0.15em] text-blue-600"
        >
          · Digital Product Passports
        </div>

        <h1
          style={{
            ...anim(0.12),
            fontWeight: 900,
            // scales with both screen width and height so it never overflows short screens
            fontSize: "clamp(2rem, min(5vw, 7.4vh), 4.5rem)",
          }}
          className="text-slate-900 mt-6 leading-[1.1] tracking-tight"
        >
         Digital Product Passports for Circularity & Trust
        </h1>

        <p
          style={anim(0.24)}
          className="text-slate-500 text-base sm:text-lg lg:text-xl mt-6 mx-auto max-w-2xl leading-relaxed"
        >
          We take you from scattered product data to a live, EU-compliant
          passport, with hands-on support at every step.
        </p>

        <div
          style={anim(0.36)}
          className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 mt-8"
        >
          <button
            onClick={goPassport}
            className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-4 rounded-full font-semibold transition-all duration-300 hover:shadow-[0_0_24px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 active:scale-95 inline-flex items-center justify-center gap-2"
          >
            See battery passport <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={goHandbook}
            className="bg-white border border-blue-300 hover:border-blue-500 text-slate-900 hover:text-blue-600 px-7 py-4 rounded-full font-semibold transition-all duration-300 hover:-translate-y-0.5 active:scale-95 inline-flex items-center justify-center"
          >
            See handbook
          </button>
        </div>

        <div
          style={anim(0.48)}
          className="flex flex-wrap justify-center gap-x-6 gap-y-3 mt-8 text-slate-600 text-sm"
        >
          {TRUST_ITEMS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <Check className="w-4 h-4 text-blue-600 flex-shrink-0" strokeWidth={2.5} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Light-mode perspective grid — starts right below the content, scrolls toward the horizon */}
      <PerspectiveGrid
        className="flex-1 min-h-[240px]"
        width="100%"
        height="auto"
        horizon={0}
        vanish={-0.29}
      />
    </section>
  );
}

export default Hero;